import { portalApi, normalizeComponentPolicyResponse } from '../redux/api/portalApi';
import { replaceRealtimeUserPermissions, replaceRealtimePermissionTemplates } from '../redux/slices/permissionsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setCriticalData } from '../redux/slices/criticalDataSlice';
import { replaceRealtimeDropdownValues } from '../redux/slices/dropdownSlice';
import useGuardedRealtimeList from './useGuardedRealtimeList';
import { setHolidays } from '../redux/slices/holidaysSlice';
import { replaceFieldDescriptions } from '../redux/slices/fieldDescriptionSlice';
import { useEffect } from 'react';
import { CRITICAL_RESOURCES, criticalDataArgs } from '../redux/api/criticalDataContract';
import { useStore } from 'react-redux';

function useCriticalList(apiKey, enabled, stateKey = apiKey) {
  const dispatch = useDispatch();
  const store = useStore();
  const user = useSelector(state => state.user?.userDetails);
  const data = useSelector(state => state.criticalData?.data?.[stateKey]);
  useGuardedRealtimeList({ apiKey, enabled, ownerSuffix: `critical:${apiKey}`,
    apply: rows => dispatch(setCriticalData({ apiKey: stateKey, data: rows })),
  });
  useEffect(() => {
    if (!enabled || !Array.isArray(data)) return;
    const field = CRITICAL_RESOURCES[stateKey].field;
    dispatch(portalApi.util.updateQueryData('getCriticalData', criticalDataArgs(store.getState()), draft => {
      draft[field] = data;
      const camelField = field[0].toLowerCase() + field.slice(1);
      if (Object.prototype.hasOwnProperty.call(draft, camelField)) draft[camelField] = data;
    }));
    if (apiKey === 'userPreferences') {
      dispatch(replaceRealtimePermissionTemplates(data));
      dispatch(replaceRealtimeUserPermissions({ rows: data, userObjectId: user?.id || user?.localAccountId }));
      dispatch(portalApi.util.updateQueryData('getUserPreferences', undefined, () => data));
    }
    if (apiKey === 'dropdownValues') dispatch(replaceRealtimeDropdownValues(data));
    if (apiKey === 'holidays') dispatch(setHolidays(data));
    if (apiKey === 'hoverDescriptions') dispatch(replaceFieldDescriptions(data));
  }, [apiKey, stateKey, data, dispatch, enabled, user, store]);
}
export default function useCriticalDataRealtime(enabled) {
  useCriticalList('dropdownValues', enabled);
  useCriticalList('hoverDescriptions', enabled);
  useCriticalList('globalVariables', enabled);
  useCriticalList('holidays', enabled);
  useCriticalList('userPreferences', enabled);
  useCriticalList('iconData', enabled);
  useCriticalList('tbdValues', enabled, 'tbdAudit');
  const dispatch = useDispatch();
  useGuardedRealtimeList({ apiKey: 'globalPermissions', enabled, ownerSuffix: 'critical:policies',
    apply: rows => {
      const policies = normalizeComponentPolicyResponse(rows);
      const publishedPolicies = policies.filter(policy =>
        String(policy?.status ?? policy?.Status ?? '').trim().toLowerCase() === 'published'
      );
      dispatch(portalApi.util.updateQueryData('getComponentPolicies', undefined, () => policies));
      dispatch(portalApi.util.updateQueryData('getComponentPolicies', { status: 'published' }, () => publishedPolicies));
    },
  });
}

export function CriticalDataRealtime() {
  const initialized = useSelector(state => state.criticalData?.initialized);
  useCriticalDataRealtime(Boolean(initialized));
  return null;
}
